import { useState } from 'react';
import { Button, Card, Input, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useContracts,
  useCreateContract,
  useSuspendContract,
  useReactivateContract,
  useCancelContract,
  type ContractPeriodicity,
} from './useContracts';

/* Aba Contratos da ficha (RF-015): RecurringContract por clientId, com
   criar/suspender/reativar/cancelar. Document com category=CONTRACT não
   aparece aqui (RN-003) — só contrato comercial persistido. */

const PERIODICITIES: Array<{ id: ContractPeriodicity; label: string }> = [
  { id: 'WEEKLY', label: 'Semanal' },
  { id: 'BIWEEKLY', label: 'Quinzenal' },
  { id: 'MONTHLY', label: 'Mensal' },
  { id: 'BIMONTHLY', label: 'Bimestral' },
  { id: 'QUARTERLY', label: 'Trimestral' },
  { id: 'SEMIANNUAL', label: 'Semestral' },
  { id: 'ANNUAL', label: 'Anual' },
];

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  CANCELLED: 'Cancelado',
};

export function ContratosDoCliente({ customerId }: { customerId: string }): JSX.Element {
  const { data, isLoading, error } = useContracts({ clientId: customerId, limit: 20 });
  const createContract = useCreateContract();
  const suspendContract = useSuspendContract();
  const reactivateContract = useReactivateContract();
  const cancelContract = useCancelContract();

  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [periodicity, setPeriodicity] = useState<ContractPeriodicity>('MONTHLY');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState('');
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const rows = data?.data ?? [];
  const busy =
    createContract.isPending ||
    suspendContract.isPending ||
    reactivateContract.isPending ||
    cancelContract.isPending;

  const submit = (): void => {
    const amount = Number(value);
    if (!startDate) {
      setFormError('Informe o início.');
      return;
    }
    if (!Number.isFinite(amount) || amount < 0) {
      setFormError('Valor deve ser ≥ 0.');
      return;
    }
    if (!description.trim()) {
      setFormError('Descreva o objeto do contrato.');
      return;
    }
    setFormError('');
    createContract.mutate(
      {
        clientId: customerId,
        startDate,
        periodicity,
        value: amount,
        items: [
          {
            productId: 'service',
            description: description.trim(),
            quantity: 1,
            unitValue: amount,
          },
        ],
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setStartDate('');
          setValue('');
          setDescription('');
        },
        onError: (err) => setFormError((err as Error).message),
      },
    );
  };

  const confirmCancel = (): void => {
    if (!cancelId || !cancelReason.trim()) {
      setFormError('O motivo do cancelamento é obrigatório.');
      return;
    }
    cancelContract.mutate(
      { id: cancelId, reason: cancelReason.trim() },
      {
        onSuccess: () => {
          setCancelId(null);
          setCancelReason('');
        },
        onError: (err) => setFormError((err as Error).message),
      },
    );
  };

  return (
    <div className="flex flex-col gap-md">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Fechar' : 'Novo contrato'}
        </Button>
      </div>

      {showForm ? (
        <Card>
          <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
            Novo contrato recorrente
          </h3>
          <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
            <Input
              id="contract-start"
              label="Início *"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <div>
              <div className="field-label">Periodicidade</div>
              <div className="flex flex-wrap gap-xs">
                {PERIODICITIES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPeriodicity(p.id)}
                    aria-pressed={periodicity === p.id}
                    className={`rounded-md border px-sm py-xs font-label-md text-label-md ${
                      periodicity === p.id
                        ? 'border-primary bg-primary-container font-semibold text-on-primary-container'
                        : 'border-outline-variant text-on-surface-variant'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <Input
              id="contract-value"
              label="Valor (R$) *"
              type="number"
              min="0"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <Input
              id="contract-description"
              label="Objeto *"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: Suporte mensal"
            />
          </div>
          {formError ? (
            <p className="mb-sm font-body-sm text-body-sm text-error">{formError}</p>
          ) : null}
          <div className="mt-sm">
            <Button variant="primary" loading={createContract.isPending} onClick={submit}>
              Salvar contrato
            </Button>
          </div>
        </Card>
      ) : null}

      {isLoading ? (
        <LoadingSkeleton count={3} height="64px" />
      ) : error ? (
        <p className="font-body-md text-body-md text-error">{(error as Error).message}</p>
      ) : rows.length === 0 ? (
        <Card>
          <p className="mb-xs mt-0 font-headline-sm text-headline-sm text-on-surface">
            Nenhum contrato
          </p>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Este cliente ainda não tem contratos recorrentes.
          </p>
        </Card>
      ) : (
        <Card padding="0">
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {rows.map((contract) => (
              <li
                key={contract.id}
                className="flex flex-col gap-sm border-b border-outline-variant p-md last:border-b-0"
              >
                <div className="flex flex-wrap items-center justify-between gap-sm">
                  <span className="font-body-md text-body-md font-semibold text-on-surface">
                    {PERIODICITIES.find((p) => p.id === contract.periodicity)?.label} ·{' '}
                    {formatCurrency(contract.value)}
                  </span>
                  <span className="font-label-sm text-label-sm text-on-surface-variant">
                    {STATUS_LABEL[contract.status] ?? contract.status}
                  </span>
                </div>
                <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                  Início {formatDate(contract.startDate)} · próxima cobrança{' '}
                  {formatDate(contract.nextBillingDate)}
                  {contract.cancellationReason ? ` · ${contract.cancellationReason}` : ''}
                </p>
                {contract.status !== 'CANCELLED' ? (
                  <div className="flex flex-wrap gap-sm">
                    {contract.status === 'ACTIVE' ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => suspendContract.mutate(contract.id)}
                        className="font-label-md text-label-md text-warning-amber hover:underline disabled:opacity-40"
                      >
                        Suspender
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => reactivateContract.mutate(contract.id)}
                        className="font-label-md text-label-md text-primary hover:underline disabled:opacity-40"
                      >
                        Reativar
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setCancelId(contract.id);
                        setCancelReason('');
                      }}
                      className="font-label-md text-label-md text-error hover:underline disabled:opacity-40"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {cancelId ? (
        <Card>
          <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
            Cancelar contrato
          </h3>
          <Input
            id="contract-cancel-reason"
            label="Motivo (obrigatório)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Ex.: cliente encerrou operação"
          />
          <div className="mt-sm flex gap-sm">
            <Button variant="outline" onClick={() => setCancelId(null)}>
              Voltar
            </Button>
            <Button
              variant="primary"
              disabled={!cancelReason.trim() || busy}
              onClick={confirmCancel}
            >
              Confirmar cancelamento
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
